import { HttpClient } from './client/httpClient';
import { CommitModel } from '../models/commitModel';
import backendHttpClient from './client/backendHttpClient';
import { CommitResponse } from './responses/commitResponse';
import apiErrorHandler from './error/apiErrorHandler';
import { ErrorHandler } from './error/errorHandler';
import { DiffModel } from '../models/diffModel';
import { DiffResponse } from './responses/diffResponse';
import { ConfigFileResponse } from './responses/configFileResponse';
import { ConfigFileModel } from '../models/configFileModel';

class CommitApi {
  constructor(private readonly httpClient: HttpClient,
              private readonly errorHandler: ErrorHandler) {}

  public fetchCommitById(commitId: string): Promise<CommitModel> {
    return this.httpClient.get<CommitResponse>(`/commits/${commitId}`)
      .then(response => this.mapToCommitModel(response))
      .catch(error => this.errorHandler.handleError(error, 'Failed to fetch commit by id'));
  }

  public fetchCommitDiff(baseCommitId: string, commitId: string): Promise<DiffModel> {
    return this.httpClient.get<DiffResponse>(`/commits/${baseCommitId}/diff/${commitId}`)
      .then(response => this.mapToDiffModel(response))
      .catch(error => this.errorHandler.handleError(error, 'Failed to fetch commits diff'));
  }

  public fetchCommitHistory(commitId: string, depth: number): Promise<CommitModel[]> {
    return this.httpClient.get<CommitResponse[]>(`/commits/${commitId}/history?depth=${depth}`)
      .then(response => response.map(it => this.mapToCommitModel(it)))
      .catch(error => this.errorHandler.handleError(error, 'Failed to fetch commit history'));
  }

  public fetchConfigFilesByCommitId(commitId: string): Promise<ConfigFileModel[]> {
    return this.httpClient.get<ConfigFileResponse[]>(`/commits/${commitId}/config-files`)
      .then(response => response.map(it => this.mapToConfigFile(it)))
      .catch(error => this.errorHandler.handleError(error, 'Failed to fetch config files by commit id'));
  }

  private mapToCommitModel(response: CommitResponse): CommitModel {
    return {
      id: response.id,
      message: response.message,
      author: response.author,
      createdDate: response.createdDate,
    };
  }

  private mapToDiffModel(response: DiffResponse): DiffModel {
    return {
      diffs: response.diffs.map((diff) => ({
        resourceName: diff.resourceName,
        status: diff.status,
        next: diff.next ? { configFile: this.mapToConfigFile(diff.next.configFile) } : undefined,
        prev: diff.prev ? { configFile:this.mapToConfigFile(diff.prev.configFile) } : undefined,
      }))
    }
  }

  private mapToConfigFile(response: ConfigFileResponse): ConfigFileModel {
    return {
      id: response.id,
      content: response.content,
      filename: response.filename,
      size: response.size,
    }
  }
}

export default new CommitApi(backendHttpClient, apiErrorHandler);
